<!-- views/CreatorProfileView.vue -->
<template>
  <div class="creator-profile">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading creator profile...</p>
    </div>

    <div v-else-if="creator" class="profile-container">
      <!-- Profile Header -->
      <div class="profile-header">
        <div class="cover-photo">
          <img
            v-if="creator.coverPhoto"
            :src="creator.coverPhoto"
            :alt="creator.displayName"
          />
          <div v-else class="cover-placeholder"></div>
        </div>

        <div class="profile-info">
          <div class="avatar-section">
            <img
              v-if="creator.photoURL"
              :src="creator.photoURL"
              :alt="creator.displayName"
              class="avatar"
            />
            <div v-else class="avatar-placeholder">
              {{ creatorInitials }}
            </div>
          </div>

          <div class="user-details">
            <div class="name-section">
              <h1>{{ creator.displayName }}</h1>
              <span class="role-badge" :class="creator.role">
                {{
                  creator.role === "creator"
                    ? "🎓 Course Creator"
                    : "📚 Learner"
                }}
              </span>
              <button
                v-if="isAuthenticated && !isOwnProfile"
                class="follow-btn"
                :class="{ following: isFollowing }"
                @click="toggleFollow"
                :disabled="followLoading"
              >
                {{
                  followLoading
                    ? "..."
                    : isFollowing
                      ? "✓ Following"
                      : "+ Follow"
                }}
              </button>
            </div>

            <p class="bio">{{ creator.bio || "No bio yet." }}</p>

            <div class="user-meta">
              <span class="meta-item">
                <span class="icon">📍</span>
                {{ creator.location || "Location not set" }}
              </span>
              <span class="meta-item">
                <span class="icon">📅</span>
                Joined {{ formatDate(creator.createdAt) }}
              </span>
              <span class="meta-item">
                <span class="icon">📚</span>
                {{ creator.createdCourses?.length || 0 }} courses
              </span>
              <span class="meta-item">
                <span class="icon">👥</span>
                {{ creator.followers?.length || 0 }} followers
              </span>
            </div>

            <!-- Social Links -->
            <div v-if="hasSocialLinks" class="social-links">
              <a
                v-for="(url, platform) in creator.socialLinks"
                :key="platform"
                :href="url"
                target="_blank"
                class="social-link"
                :title="platform"
              >
                {{ getSocialIcon(platform) }}
              </a>
            </div>

            <!-- Skills -->
            <div v-if="creator.skills?.length > 0" class="skills">
              <span class="skill" v-for="skill in creator.skills" :key="skill">
                #{{ skill }}
              </span>
            </div>
          </div>

          <div class="profile-stats">
            <div class="stat" @click="showFollowers = true">
              <span class="number">{{ creator.followers?.length || 0 }}</span>
              <span class="label">Followers</span>
            </div>
            <div class="stat" @click="showFollowing = true">
              <span class="number">{{ creator.following?.length || 0 }}</span>
              <span class="label">Following</span>
            </div>
            <div class="stat">
              <span class="number">{{
                creator.createdCourses?.length || 0
              }}</span>
              <span class="label">Courses</span>
            </div>
            <div class="stat">
              <span class="number">{{ totalStudents }}</span>
              <span class="label">Students</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Profile Tabs -->
      <div class="profile-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.icon }} {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Courses Tab -->
        <div v-if="activeTab === 'courses'" class="courses-tab">
          <div class="tab-header">
            <h3>All Courses ({{ creator.createdCourses?.length || 0 }})</h3>
            <div class="filter-options">
              <button
                v-for="filter in courseFilters"
                :key="filter.id"
                class="filter-btn"
                :class="{ active: activeCourseFilter === filter.id }"
                @click="activeCourseFilter = filter.id"
              >
                {{ filter.label }}
              </button>
            </div>
          </div>

          <div v-if="filteredCourses.length === 0" class="empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h3>No courses yet</h3>
            <p>{{ creator.displayName }} hasn't created any courses yet.</p>
          </div>

          <div v-else class="courses-grid">
            <CourseCard
              v-for="course in filteredCourses"
              :key="course.id"
              :course="course"
              @click="$router.push(`/course/${course.id}`)"
            />
          </div>
        </div>

        <!-- About Tab -->
        <div v-if="activeTab === 'about'" class="about-tab">
          <div class="about-section">
            <h3>About {{ creator.displayName }}</h3>
            <div class="about-content">
              <p v-if="creator.bio" class="bio-text">{{ creator.bio }}</p>
              <p v-else class="no-bio">This creator hasn't added a bio yet.</p>
            </div>
          </div>

          <div v-if="creator.skills?.length > 0" class="skills-section">
            <h3>Skills & Expertise</h3>
            <div class="skills-list">
              <span
                class="skill-tag"
                v-for="skill in creator.skills"
                :key="skill"
              >
                {{ skill }}
              </span>
            </div>
          </div>

          <div v-if="creator.interests?.length > 0" class="interests-section">
            <h3>Interests</h3>
            <div class="interests-list">
              <span
                class="interest-tag"
                v-for="interest in creator.interests"
                :key="interest"
              >
                {{ interest }}
              </span>
            </div>
          </div>

          <div v-if="hasSocialLinks" class="social-section">
            <h3>Connect</h3>
            <div class="social-grid">
              <a
                v-for="(url, platform) in creator.socialLinks"
                :key="platform"
                :href="url"
                target="_blank"
                class="social-card"
              >
                <span class="social-icon">{{ getSocialIcon(platform) }}</span>
                <span class="social-platform">{{ platform }}</span>
                <span class="social-url">{{ url }}</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Reviews Tab -->
        <div v-if="activeTab === 'reviews'" class="reviews-tab">
          <div class="tab-header">
            <h3>Reviews & Ratings</h3>
            <div class="rating-summary">
              <span class="average">⭐ {{ averageRating }}</span>
              <span class="count">({{ totalReviews }} reviews)</span>
            </div>
          </div>

          <div v-if="allReviews.length === 0" class="empty-state">
            <p>No reviews yet for this creator's courses.</p>
          </div>

          <div v-else class="reviews-list">
            <div
              v-for="review in allReviews"
              :key="review.id"
              class="review-item"
            >
              <div class="review-header">
                <div class="reviewer-info">
                  <img
                    :src="
                      review.userAvatar ||
                      `https://ui-avatars.com/api/?name=${review.userName}&background=FF8000&color=fff`
                    "
                    :alt="review.userName"
                  />
                  <div>
                    <span class="reviewer-name">{{ review.userName }}</span>
                    <span class="review-date">{{
                      formatDate(review.createdAt)
                    }}</span>
                  </div>
                </div>
                <div class="review-rating">
                  {{ "⭐".repeat(review.rating) }}
                </div>
              </div>
              <p class="review-text">{{ review.text }}</p>
              <div v-if="review.courseTitle" class="review-course">
                <span>📚 Course: </span>
                <router-link :to="`/course/${review.courseId}`">{{
                  review.courseTitle
                }}</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="error-state">
      <svg
        width="80"
        height="80"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <h2>Creator Not Found</h2>
      <p>The creator you're looking for doesn't exist.</p>
      <button class="btn-primary" @click="$router.push('/explore')">
        Explore Creators
      </button>
    </div>

    <!-- Followers/Following Modals -->
    <div
      v-if="showFollowers"
      class="modal-overlay"
      @click.self="showFollowers = false"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3>Followers</h3>
          <button class="close-btn" @click="showFollowers = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="creator.followers?.length === 0" class="empty-modal">
            <p>No followers yet.</p>
          </div>
          <div v-else class="user-list">
            <div
              v-for="follower in creator.followers"
              :key="follower.id"
              class="user-item"
            >
              <img
                :src="
                  follower.photoURL ||
                  `https://ui-avatars.com/api/?name=${follower.displayName}&background=FF8000&color=fff`
                "
                :alt="follower.displayName"
                @click="$router.push(`/profile/${follower.id}`)"
              />
              <span @click="$router.push(`/profile/${follower.id}`)">{{
                follower.displayName
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showFollowing"
      class="modal-overlay"
      @click.self="showFollowing = false"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3>Following</h3>
          <button class="close-btn" @click="showFollowing = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="creator.following?.length === 0" class="empty-modal">
            <p>Not following anyone yet.</p>
          </div>
          <div v-else class="user-list">
            <div
              v-for="following in creator.following"
              :key="following.id"
              class="user-item"
            >
              <img
                :src="
                  following.photoURL ||
                  `https://ui-avatars.com/api/?name=${following.displayName}&background=FF8000&color=fff`
                "
                :alt="following.displayName"
                @click="$router.push(`/profile/${following.id}`)"
              />
              <span @click="$router.push(`/profile/${following.id}`)">{{
                following.displayName
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCourseStore } from "@/stores/course";
import { useUserStore } from "@/stores/user";
import CourseCard from "@/components/courses/CourseCard.vue";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const courseStore = useCourseStore();
const userStore = useUserStore();

// State
const creator = ref(null);
const loading = ref(true);
const followLoading = ref(false);
const activeTab = ref("courses");
const activeCourseFilter = ref("all");
const showFollowers = ref(false);
const showFollowing = ref(false);

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated);
const currentUser = computed(() => authStore.user);
const isOwnProfile = computed(() => {
  return currentUser.value?.id === creator.value?.id;
});

const isFollowing = computed(() => {
  if (!creator.value || !currentUser.value) return false;
  return creator.value.followers?.some((f) => f.id === currentUser.value.id);
});

const creatorInitials = computed(() => {
  if (!creator.value?.displayName) return "?";
  return creator.value.displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
});

const hasSocialLinks = computed(() => {
  return (
    creator.value?.socialLinks &&
    Object.keys(creator.value.socialLinks).length > 0
  );
});

const totalStudents = computed(() => {
  if (!creator.value?.createdCourses) return 0;
  return creator.value.createdCourses.reduce(
    (acc, course) => acc + (course.totalStudents || 0),
    0,
  );
});

const filteredCourses = computed(() => {
  if (!creator.value?.createdCourses) return [];

  let courses = [...creator.value.createdCourses];

  switch (activeCourseFilter.value) {
    case "published":
      return courses.filter((c) => c.isPublished);
    case "free":
      return courses.filter((c) => c.price === 0);
    case "paid":
      return courses.filter((c) => c.price > 0);
    default:
      return courses;
  }
});

const allReviews = computed(() => {
  if (!creator.value?.createdCourses) return [];
  const reviews = [];
  creator.value.createdCourses.forEach((course) => {
    if (course.reviews) {
      course.reviews.forEach((review) => {
        reviews.push({
          ...review,
          courseId: course.id,
          courseTitle: course.title,
        });
      });
    }
  });
  return reviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
});

const totalReviews = computed(() => allReviews.value.length);

const averageRating = computed(() => {
  if (totalReviews.value === 0) return "N/A";
  const total = allReviews.value.reduce((sum, r) => sum + r.rating, 0);
  return (total / totalReviews.value).toFixed(1);
});

// Tabs
const tabs = [
  { id: "courses", label: "Courses", icon: "📚" },
  { id: "about", label: "About", icon: "ℹ️" },
  { id: "reviews", label: "Reviews", icon: "⭐" },
];

const courseFilters = [
  { id: "all", label: "All" },
  { id: "published", label: "Published" },
  { id: "free", label: "Free" },
  { id: "paid", label: "Paid" },
];

// Methods
const loadCreator = async (userId) => {
  loading.value = true;
  try {
    const result = await userStore.getUserProfile(userId);
    if (result.success) {
      creator.value = result.data;

      // Load creator's courses
      await courseStore.fetchCreatorCourses(userId);

      // Update creator's courses from store
      if (creator.value) {
        creator.value.createdCourses = courseStore.courses.filter(
          (c) => c.creatorId === userId,
        );
      }
    } else {
      creator.value = null;
    }
  } catch (error) {
    console.error("Error loading creator:", error);
    creator.value = null;
  } finally {
    loading.value = false;
  }
};

const toggleFollow = async () => {
  if (!isAuthenticated.value) {
    router.push("/?redirect=" + route.fullPath);
    return;
  }

  followLoading.value = true;
  try {
    const result = await userStore.toggleFollow(creator.value.id);
    if (result.success) {
      // Update local state
      if (isFollowing.value) {
        // Unfollow
        creator.value.followers = creator.value.followers.filter(
          (f) => f.id !== currentUser.value.id,
        );
      } else {
        // Follow
        creator.value.followers.push({
          id: currentUser.value.id,
          displayName: currentUser.value.displayName,
          photoURL: currentUser.value.photoURL,
        });
      }
    }
  } catch (error) {
    console.error("Follow error:", error);
  } finally {
    followLoading.value = false;
  }
};

const getSocialIcon = (platform) => {
  const icons = {
    github: "🐙",
    website: "🌐",
    linkedin: "💼",
    twitter: "🐦",
    youtube: "▶️",
    instagram: "📸",
    facebook: "📘",
    tiktok: "🎵",
  };
  return icons[platform] || "🔗";
};

const formatDate = (timestamp) => {
  if (!timestamp) return "Recently";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Watch route changes
watch(
  () => route.params.id,
  (newId) => {
    if (newId) {
      loadCreator(newId);
    }
  },
  { immediate: true },
);

// Load creator on mount
onMounted(() => {
  const userId = route.params.id;
  if (userId) {
    loadCreator(userId);
  }
});
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;
$hover-orange: #cc5800;

.creator-profile {
  margin-top: 72px;
  min-height: 100vh;
  background: #f0f2f5;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid $primary-orange;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    margin-top: 16px;
    color: #868e96;
  }
}

.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.profile-header {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.cover-photo {
  position: relative;
  height: 200px;
  background: linear-gradient(135deg, $primary-blue, #003355);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .cover-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, $primary-blue, #003355);
  }
}

.profile-info {
  padding: 24px;
  position: relative;

  .avatar-section {
    margin-top: -80px;
    display: inline-block;

    .avatar {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      border: 4px solid #fff;
      object-fit: cover;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .avatar-placeholder {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      border: 4px solid #fff;
      background: $primary-orange;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 36px;
      font-weight: 700;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  .user-details {
    margin-top: 16px;

    .name-section {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 8px;

      h1 {
        margin: 0;
        font-size: 28px;
        color: #1a1a1a;
      }

      .role-badge {
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 600;

        &.creator {
          background: rgba(255, 128, 0, 0.1);
          color: $primary-orange;
        }

        &.learner {
          background: rgba(0, 123, 255, 0.1);
          color: #007bff;
        }
      }

      .follow-btn {
        padding: 6px 16px;
        border: 2px solid $primary-orange;
        border-radius: 20px;
        background: transparent;
        color: $primary-orange;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        font-size: 14px;

        &:hover:not(:disabled) {
          background: $primary-orange;
          color: #fff;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        &.following {
          background: $primary-orange;
          color: #fff;

          &:hover:not(:disabled) {
            background: #dc3545;
            border-color: #dc3545;
          }
        }
      }
    }

    .bio {
      color: #495057;
      font-size: 16px;
      margin: 0 0 12px 0;
      line-height: 1.6;
      max-width: 600px;
    }

    .user-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-bottom: 12px;

      .meta-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: #868e96;

        .icon {
          font-size: 18px;
        }
      }
    }

    .social-links {
      display: flex;
      gap: 12px;
      margin-bottom: 12px;

      .social-link {
        font-size: 24px;
        text-decoration: none;
        transition: all 0.2s ease;

        &:hover {
          transform: scale(1.2);
        }
      }
    }

    .skills {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .skill {
        padding: 4px 12px;
        background: #f1f3f5;
        border-radius: 12px;
        font-size: 13px;
        color: #495057;
      }
    }
  }

  .profile-stats {
    display: flex;
    gap: 32px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f1f3f5;

    .stat {
      text-align: center;
      cursor: pointer;

      .number {
        display: block;
        font-weight: 700;
        font-size: 20px;
        color: #1a1a1a;
      }

      .label {
        font-size: 14px;
        color: #868e96;
      }

      &:hover {
        .number {
          color: $primary-orange;
        }
      }
    }
  }
}

.profile-tabs {
  display: flex;
  background: #fff;
  border-radius: 0 0 16px 16px;
  padding: 0 24px;
  margin-top: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow-x: auto;

  .tab {
    padding: 16px 20px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 14px;
    font-weight: 600;
    color: #868e96;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;

    &:hover {
      color: #1a1a1a;
    }

    &.active {
      color: $primary-orange;
      border-bottom-color: $primary-orange;
    }
  }
}

.tab-content {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: 400px;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;

  h3 {
    margin: 0;
    color: #1a1a1a;
  }

  .filter-options {
    display: flex;
    gap: 8px;

    .filter-btn {
      padding: 4px 12px;
      background: #f1f3f5;
      border: none;
      border-radius: 12px;
      font-size: 13px;
      color: #868e96;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #e9ecef;
      }

      &.active {
        background: $primary-orange;
        color: #fff;
      }
    }
  }

  .rating-summary {
    .average {
      font-size: 18px;
      font-weight: 700;
      color: #1a1a1a;
    }

    .count {
      font-size: 14px;
      color: #868e96;
      margin-left: 4px;
    }
  }
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;

  svg {
    color: #adb5bd;
    margin-bottom: 16px;
  }

  h3 {
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  p {
    color: #868e96;
  }
}

/* About Tab */
.about-section,
.skills-section,
.interests-section,
.social-section {
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    font-size: 18px;
    color: #1a1a1a;
    margin: 0 0 12px 0;
  }
}

.about-content {
  .bio-text {
    font-size: 16px;
    line-height: 1.8;
    color: #495057;
    margin: 0;
  }

  .no-bio {
    color: #868e96;
    font-style: italic;
  }
}

.skills-list,
.interests-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .skill-tag,
  .interest-tag {
    padding: 6px 16px;
    background: #f1f3f5;
    border-radius: 16px;
    font-size: 14px;
    color: #495057;
  }

  .interest-tag {
    background: rgba(255, 128, 0, 0.1);
    color: $primary-orange;
  }
}

.social-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;

  .social-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      background: #f1f3f5;
      transform: translateY(-2px);
    }

    .social-icon {
      font-size: 24px;
    }

    .social-platform {
      font-weight: 600;
      color: #1a1a1a;
      min-width: 60px;
    }

    .social-url {
      color: $primary-orange;
      word-break: break-all;
      font-size: 13px;
    }
  }
}

/* Reviews Tab */
.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .review-item {
    padding: 16px;
    background: #f8f9fa;
    border-radius: 12px;

    .review-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;

      .reviewer-info {
        display: flex;
        align-items: center;
        gap: 12px;

        img {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }

        .reviewer-name {
          font-weight: 600;
          color: #1a1a1a;
          display: block;
        }

        .review-date {
          font-size: 12px;
          color: #868e96;
        }
      }

      .review-rating {
        font-size: 16px;
      }
    }

    .review-text {
      margin: 0 0 8px 0;
      color: #495057;
      line-height: 1.6;
    }

    .review-course {
      font-size: 13px;
      color: #868e96;

      a {
        color: $primary-orange;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;

  h3 {
    margin: 0;
    color: #1a1a1a;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 28px;
    color: #868e96;
    cursor: pointer;

    &:hover {
      color: #1a1a1a;
    }
  }
}

.modal-body {
  padding: 24px;
  max-height: calc(80vh - 80px);
  overflow-y: auto;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .user-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f9fa;
    }

    img {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }

    span {
      font-weight: 500;
      color: #1a1a1a;
    }
  }
}

.empty-modal {
  text-align: center;
  padding: 40px 20px;
  color: #868e96;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  text-align: center;

  svg {
    color: #adb5bd;
    margin-bottom: 16px;
  }

  h2 {
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  p {
    color: #868e96;
    margin-bottom: 24px;
  }

  .btn-primary {
    padding: 12px 32px;
    background: $primary-orange;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;

    &:hover {
      background: $hover-orange;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .profile-container {
    padding: 16px;
  }

  .profile-info {
    .user-details {
      .name-section {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }

  .profile-tabs {
    padding: 0 16px;

    .tab {
      padding: 12px 16px;
      font-size: 13px;
    }
  }

  .tab-content {
    padding: 16px;
  }

  .courses-grid {
    grid-template-columns: 1fr;
  }

  .social-grid {
    grid-template-columns: 1fr;
  }

  .profile-stats {
    gap: 16px;
    flex-wrap: wrap;
  }
}
</style>
